/**
 * PlatformGraph — pure reachability + ceiling math for the NavigationSystem.
 *
 * Extracted from the Node-side navigator (`tools/qa/playthrough-record.mjs`).
 * All functions are side-effect free so they can be unit-tested without a
 * Babylon scene.
 *
 * Vocabulary:
 *   - Platform: an AABB with name, minX/maxX/minY/maxY.
 *   - Ceiling: a merged group of platforms directly above a query point
 *     whose lower surfaces form an obstruction span.
 *   - Route: an ordered list of platforms the cat must traverse in order
 *     to reach a target that's out of direct jump/drop reach.
 */
import type { PlatformAABB } from "../../qa/types";
import { PLAYER, PHYSICS } from "../../config/GameConfig";

export type CatKind = "minna" | "basil";

// Jump-reach constants. Kept alongside the planner rather than in
// GameConfig so the navigator's reach assumptions travel as a unit with
// the planning logic. When these change, re-tune both sides together.
export const NORMAL_JUMP_RISE = 2.7;
export const OVLEAP_RISE = 16.0;
export const NORMAL_JUMP_X = 5.0;
export const OVLEAP_JUMP_X = 8.0;
export const DROP_X = 6.0;

// Projectile model used by solveLaunchX.
// Derived from GameConfig so per-cat tuning (Task 8) can diverge without
// hand-duplicating literals. Default values: VY_INIT=9, GRAVITY_MAG=15, WALK_VX=5.
export const VY_INIT: number = PLAYER.JUMP_IMPULSE;         // 9
export const GRAVITY_MAG: number = Math.abs(PHYSICS.GRAVITY_Y); // 15
export const WALK_VX: number = PLAYER.MOVE_SPEED;           // 5

// OvLeap projectile model — JUMP_IMPULSE * leapMultiplier in the
// OverclockedLeap ability. Keep in sync with
// `src-babylon/abilities/basil/OverclockedLeap.ts` / `config/GameConfig.ts`.
export const OVLEAP_VY_INIT = 22.5;

// Synthetic ground platform covering the typical level X range. Inserted
// into plan candidates so a cat standing on bare ground can plan jumps
// onto floating platforms.
export const GROUND_PLATFORM: PlatformAABB = {
  name: "__ground__",
  minX: -200,
  maxX: 200,
  minY: -0.5,
  maxY: 0,
  minZ: -100,
  maxZ: 100,
};

export interface CeilingSpan {
  /** Lowest-minX mesh name in the merged span (for logging). */
  name: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  /** Names of every platform merged into the span. */
  merged: string[];
}

export function filterPlannable(platforms: PlatformAABB[]): PlatformAABB[] {
  const out: PlatformAABB[] = [];
  for (const p of platforms) {
    if (!Number.isFinite(p.minX)) continue;
    if (!Number.isFinite(p.maxY)) continue;
    if (p.maxX - p.minX <= 0.5) continue;
    out.push(p);
  }
  out.push(GROUND_PLATFORM);
  return out;
}

/** Return the highest platform whose top is at or below (x, y). */
export function platformAt(
  platforms: PlatformAABB[],
  x: number,
  y: number,
): PlatformAABB | null {
  let best: PlatformAABB | null = null;
  for (const p of platforms) {
    if (x < p.minX - 0.5 || x > p.maxX + 0.5) continue;
    if (p.maxY > y + 0.3) continue;
    if (!best || p.maxY > best.maxY) best = p;
  }
  return best;
}

/**
 * Return a merged ceiling span that obstructs upward motion from (x, y).
 *
 * Merge semantics match the Node-side navigator: start with platforms
 * directly above (x, y); expand the X range to include adjacent platforms
 * whose top matches the anchor top or whose bottom sits near the span's
 * bottom. The merge prevents the navigator from oscillating at boundaries
 * between overlapping platform meshes.
 */
export function ceilingAbove(
  platforms: PlatformAABB[],
  x: number,
  y: number,
): CeilingSpan | null {
  const matches = platforms.filter(
    (p) => p.minX <= x && p.maxX >= x && p.minY > y + 0.5 && p.minY <= y + 4.0,
  );
  if (matches.length === 0) return null;

  matches.sort((a, b) => a.minY - b.minY);
  const anchor = matches[0];
  let spanMinX = anchor.minX;
  let spanMaxX = anchor.maxX;
  let spanMinY = anchor.minY;
  const anchorTopY = anchor.maxY;
  const related = [anchor.name];

  let grew = true;
  while (grew) {
    grew = false;
    for (const p of platforms) {
      if (related.includes(p.name)) continue;
      if (p.maxX < spanMinX - 0.05) continue;
      if (p.minX > spanMaxX + 0.05) continue;
      if (p.minY < y + 0.5) continue;
      if (p.minY > y + 4.0) continue;
      const topNear = Math.abs(p.maxY - anchorTopY) < 1.0;
      const bottomNear = Math.abs(p.minY - spanMinY) < 1.5;
      if (!topNear && !bottomNear) continue;
      spanMinX = Math.min(spanMinX, p.minX);
      spanMaxX = Math.max(spanMaxX, p.maxX);
      spanMinY = Math.min(spanMinY, p.minY);
      related.push(p.name);
      grew = true;
    }
  }

  return {
    name: anchor.name,
    minX: spanMinX,
    maxX: spanMaxX,
    minY: spanMinY,
    maxY: anchorTopY,
    merged: related,
  };
}

/**
 * Vertical clearance from (x, y) up to the underside of the nearest
 * ceiling directly above. Returns Infinity when nothing is overhead — use
 * that to distinguish "fully clear sky" from "has a ceiling, just far".
 *
 * Duplicates a little of ceilingAbove's scan but intentionally does NOT
 * apply the y+4.0 merge-range cap: the CatNavigator's commit-clear
 * check needs to know about ceilings that are arbitrarily far above so
 * it can distinguish "cat slipped out of one ceiling span and is now
 * under a different one" from "cat has fully cleared all overhead
 * obstructions." ceilingAbove's 4.0u cap is fine for the escape-edge
 * math (jumps can't reach higher than that anyway) but would hide
 * persistent obstructions from the clearance check.
 */
export function distanceToCeilingAbove(
  platforms: PlatformAABB[],
  x: number,
  y: number,
): number {
  let best = Infinity;
  for (const p of platforms) {
    if (x < p.minX || x > p.maxX) continue;
    if (p.minY <= y + 0.5) continue;
    const gap = p.minY - y;
    if (gap < best) best = gap;
  }
  return best;
}

function blockedByCeiling(
  candidates: PlatformAABB[],
  a: PlatformAABB,
  b: PlatformAABB,
): boolean {
  if (b.maxY <= a.maxY) return false;
  const lowX = Math.min(a.maxX, b.minX);
  const highX = Math.max(a.minX, b.maxX);
  for (const c of candidates) {
    if (c === a || c === b) continue;
    if (c.minY <= a.maxY + 0.5) continue;
    if (c.minY >= b.maxY) continue;
    if (c.maxX <= lowX + 0.2) continue;
    if (c.minX >= highX - 0.2) continue;
    return true;
  }
  return false;
}

/**
 * Sum of X-overlap between a platform's top surface and any ceiling
 * sitting within the navigator's escape-trigger range (up to y+4 above
 * the surface — matches ceilingAbove's merge cap).
 *
 * Used by planRoute to prefer routes whose intermediate platforms don't
 * force the cat to walk under ceilings. Zero means the cat can cross the
 * whole platform without triggering a ceiling-escape commit.
 */
export function ceilingWalkPenalty(
  plat: PlatformAABB,
  platforms: PlatformAABB[],
): number {
  let total = 0;
  const topY = plat.maxY;
  for (const c of platforms) {
    if (c === plat) continue;
    if (c.minY <= topY + 0.5) continue;
    if (c.minY > topY + 4.0) continue;
    const lo = Math.max(plat.minX, c.minX);
    const hi = Math.min(plat.maxX, c.maxX);
    if (hi > lo) total += hi - lo;
  }
  return total;
}

/**
 * Dijkstra a reachability graph to find the best sequence of platforms
 * that lets `cat` step from (startX, startY) to (endX, endY).
 *
 * Best = minimum sum of `ceilingWalkPenalty` across platforms the cat
 * must cross, with hop-count as the tiebreaker. BFS shortest-hop was
 * the old policy; the navigator's ceiling-escape commit hard-caps at
 * ESCAPE_MAX_DISTANCE_U=8u of under-ceiling walking, so routes that
 * traverse narrow under-ceiling platforms reliably fail fast. Switching
 * to penalty-primary routing makes the planner prefer the upper tier
 * (OvLeap up over a ceiling) when one exists, falling back to shortest-
 * hop behavior when all candidate routes share a penalty.
 *
 * Planner only emits NORMAL jumps + drops — OvLeap is cat=basil and
 * NORMAL_JUMP_RISE < dh ≤ OVLEAP_RISE with dx ≤ OVLEAP_JUMP_X, but the
 * navigator has no built-in OvLeap recipe in `goTo`, so those edges are
 * effectively only reachable via an explicit trace `traverse` step.
 */
export function planRoute(
  platforms: PlatformAABB[],
  cat: CatKind,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  normalJumpRise: number = NORMAL_JUMP_RISE,
  jumpX: number = NORMAL_JUMP_X,
): PlatformAABB[] {
  const candidates = filterPlannable(platforms);
  if (candidates.length === 0) return [];
  const startPlat = platformAt(candidates, startX, startY);
  // For the end platform, use `endY` directly so ground-level targets
  // (lens on a platform surface, pressure plate, NPC on floor) resolve
  // to the platform they sit on, not one tier up. The old Node-side
  // navigator added +1.5 here to snag "target just above platform top"
  // cases like high pedestals — but platformAt's built-in +0.3
  // tolerance already handles that, and the extra 1.5 overshoots for
  // on-platform targets (makes the planner route the cat UP when it
  // should walk ON the same platform as the target).
  const endPlat = platformAt(candidates, endX, endY);
  if (!startPlat || !endPlat) return [];
  if (startPlat === endPlat) return [];

  const edge = (a: PlatformAABB, b: PlatformAABB): boolean => {
    if (a === b) return false;
    const dh = b.maxY - a.maxY;
    const dx = Math.max(0, Math.max(a.minX, b.minX) - Math.min(a.maxX, b.maxX));
    let reach: boolean;
    if (dh <= 0) reach = dx <= DROP_X;
    else if (dh <= normalJumpRise) reach = dx <= jumpX;
    else if (cat === "basil" && dh <= OVLEAP_RISE) reach = dx <= OVLEAP_JUMP_X;
    else reach = false;
    if (!reach) return false;
    if (blockedByCeiling(candidates, a, b)) return false;
    return true;
  };

  interface State {
    plat: PlatformAABB;
    path: PlatformAABB[];
    penalty: number;
  }
  // seenBest[name] tracks the best (penalty, hops) pair we've relaxed
  // to each node — a new candidate only enters the frontier if it
  // strictly improves one dimension without regressing the other.
  const seenBest = new Map<string, { penalty: number; hops: number }>();
  seenBest.set(startPlat.name, { penalty: 0, hops: 1 });
  const frontier: State[] = [{ plat: startPlat, path: [startPlat], penalty: 0 }];

  while (frontier.length > 0) {
    // Small graphs (≤ ~60 platforms per level) make a linear scan for
    // the minimum cheaper than maintaining a heap. Tiebreak composite
    // priority by hops so equal-penalty routes fall back to BFS order.
    let bestIdx = 0;
    for (let i = 1; i < frontier.length; i++) {
      const a = frontier[i];
      const b = frontier[bestIdx];
      if (a.penalty < b.penalty) bestIdx = i;
      else if (a.penalty === b.penalty && a.path.length < b.path.length) bestIdx = i;
    }
    const [cur] = frontier.splice(bestIdx, 1);
    if (cur.plat === endPlat) return cur.path;

    for (const next of candidates) {
      if (!edge(cur.plat, next)) continue;
      const nextPenalty = cur.penalty + ceilingWalkPenalty(next, candidates);
      const nextHops = cur.path.length + 1;
      const prior = seenBest.get(next.name);
      if (prior) {
        if (prior.penalty < nextPenalty) continue;
        if (prior.penalty === nextPenalty && prior.hops <= nextHops) continue;
      }
      seenBest.set(next.name, { penalty: nextPenalty, hops: nextHops });
      frontier.push({ plat: next, path: [...cur.path, next], penalty: nextPenalty });
    }
  }
  return [];
}

/**
 * Projectile-solved launch X — where should the cat stand on `here` so a
 * normal jump arcs to the center of `wp`? Returns null if the target is
 * unreachable from `here` in one normal jump, or `here` is too narrow to
 * host the solved position.
 *
 * Math mirrors the Node-side `solveLaunchX`:
 *   y(t) = vy*t - 0.5*g*t² - dY = 0
 *   7.5t² - 9t + dY = 0, discriminant 81 - 30*dY
 *   descending root: t2 = (9 + sqrtD) / 15
 *   arc = WALK_VX * t2
 *   launchX = wpCenterX ∓ arc   (− for east, + for west)
 *   clamped to [here.minX + 0.3, here.maxX - 0.3]
 *
 * @param gravityMag - Optional per-cat gravity magnitude (defaults to GRAVITY_MAG).
 * @param walkVx     - Optional per-cat walk speed (defaults to WALK_VX).
 */
export function solveLaunchX(
  catY: number,
  here: PlatformAABB,
  wp: PlatformAABB,
  platforms?: PlatformAABB[],
  gravityMag: number = GRAVITY_MAG,
  walkVx: number = WALK_VX,
): number | null {
  return solveLaunchXWithImpulse(catY, here, wp, VY_INIT, platforms, gravityMag, walkVx);
}

/**
 * OvLeap variant — same projectile math but with a taller vy impulse
 * (JUMP_IMPULSE * leapMultiplier ≈ 22.5) so the arc can reach targets
 * up to ~16u above the launch platform. Used by the navigator when
 * `dh > NORMAL_JUMP_RISE` and the cat is basil.
 *
 * @param gravityMag   - Optional per-cat gravity magnitude (defaults to GRAVITY_MAG).
 * @param walkVx       - Optional per-cat walk speed (defaults to WALK_VX).
 * @param ovLeapVyInit - Optional per-cat OvLeap impulse (defaults to OVLEAP_VY_INIT).
 */
export function solveOvLeapLaunchX(
  catY: number,
  here: PlatformAABB,
  wp: PlatformAABB,
  platforms?: PlatformAABB[],
  gravityMag: number = GRAVITY_MAG,
  walkVx: number = WALK_VX,
  ovLeapVyInit: number = OVLEAP_VY_INIT,
): number | null {
  return solveLaunchXWithImpulse(catY, here, wp, ovLeapVyInit, platforms, gravityMag, walkVx);
}

function solveLaunchXWithImpulse(
  catY: number,
  here: PlatformAABB,
  wp: PlatformAABB,
  vyInit: number,
  platforms?: PlatformAABB[],
  gravityMag: number = GRAVITY_MAG,
  walkVx: number = WALK_VX,
): number | null {
  const Y_LAND = wp.maxY + 0.8;
  const dY = Y_LAND - catY;
  const disc = vyInit * vyInit - 2 * gravityMag * dY;
  if (disc < 0) return null;
  const sqrtD = Math.sqrt(disc);
  const tDescend = (vyInit + sqrtD) / gravityMag;
  if (!Number.isFinite(tDescend) || tDescend <= 0) return null;

  const wpCenterX = (wp.minX + wp.maxX) / 2;
  const hereCenterX = (here.minX + here.maxX) / 2;
  const east = wpCenterX > hereCenterX;
  const arc = walkVx * tDescend;
  const launchXRaw = east ? wpCenterX - arc : wpCenterX + arc;

  const safeMin = here.minX + 0.3;
  const safeMax = here.maxX - 0.3;
  if (safeMax <= safeMin) return null;
  const clamped = Math.max(safeMin, Math.min(safeMax, launchXRaw));

  // When the launchX falls under an overhead ceiling, the navigator's
  // escape-commit state machine will fight the walk-to-launch and
  // oscillate (observed on GROUND → plat1 jumps where the "ideal"
  // launchX lands directly under plat2's west overhang). Post-process
  // by sliding launchX toward the OPPOSITE side of the ceiling span
  // from the target — the arc then threads the gap cleanly. Only
  // applied when platforms is supplied; existing call sites without
  // the arg get BFS-era behavior.
  if (platforms) {
    const overhead = ceilingAbove(platforms, clamped, catY);
    if (overhead) {
      // Pick the ceiling edge on the side AWAY from the target so
      // the resulting arc still spans toward wp.
      const candidateX = east
        ? overhead.minX - 0.3 // target east → launch west of ceiling
        : overhead.maxX + 0.3; // target west → launch east of ceiling
      if (
        candidateX >= safeMin &&
        candidateX <= safeMax &&
        ceilingAbove(platforms, candidateX, catY) === null
      ) {
        const landX = east ? candidateX + arc : candidateX - arc;
        // Only accept if the refined arc still lands somewhere on wp.
        if (landX >= wp.minX - 0.5 && landX <= wp.maxX + 0.5) {
          return candidateX;
        }
      }
    }
  }

  return clamped;
}
